import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jobCardsApi } from "@/services/api";

interface JobCard {
  id: number;
  jobNo: string;
  title: string;
  status: string;
  progress: number;
}

const statusConfig = [
  { key: "Pending",     color: "#f59e0b", bg: "#fffbeb", icon: "ri-time-line",            label: "Pending", query: "status=Pending" },
  { key: "In Progress", color: "#d97706", bg: "#fef3c7", icon: "ri-loader-4-line",         label: "In Progress", query: "status=In Progress" },
  { key: "Completed",   color: "#b45309", bg: "#fef9ee", icon: "ri-checkbox-circle-line",  label: "Completed", query: "status=Completed" },
  { key: "Delivered",   color: "#92400e", bg: "#fef3c7", icon: "ri-truck-line",            label: "Delivered", query: "status=Delivered" },
];

export default function JobStatusOverview() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const result = await jobCardsApi.getAll({ limit: 50 });
        const list = result?.data || result || [];
        setJobs(Array.isArray(list) ? list : []);
      } catch {
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const total = jobs.length || 1;

  return (
    <div className="bg-white rounded-xl border border-amber-100/60 p-4 md:p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900 text-base">Job Status Overview</h3>
          <p className="text-xs text-gray-500 mt-0.5">{jobs.length} total jobs this month</p>
        </div>
        <button
          onClick={() => navigate("/job-cards")}
          className="text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap transition-colors"
          style={{ color: "#d97706" }}
        >
          View all →
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-3 rounded-xl animate-pulse" style={{ background: "#fef3c7" }}>
              <div className="h-3 rounded w-16 mb-2" style={{ background: "#fde68a" }} />
              <div className="h-7 rounded w-8 mb-1" style={{ background: "#fde68a" }} />
              <div className="h-2.5 rounded w-12" style={{ background: "#fde68a" }} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Status cards grid */}
          <div className="grid grid-cols-2 gap-2.5 mb-4">
            {statusConfig.map((s) => {
              const count = jobs.filter((j) => j.status === s.key).length;
              const pct = Math.round((count / total) * 100);
              return (
                <div
                  key={s.key}
                  onClick={() => navigate(`/job-cards?${s.query}`)}
                  className="p-3 rounded-xl cursor-pointer transition-all hover:shadow-sm"
                  style={{ background: s.bg, border: `1px solid ${s.color}20` }}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ background: s.color + "20" }}>
                      <i className={`${s.icon} text-xs`} style={{ color: s.color }} />
                    </span>
                    <span className="text-xs font-medium text-gray-600 truncate">{s.label}</span>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: s.color }}>{count}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{pct}% of total</p>
                </div>
              );
            })}
          </div>

          {/* Stacked progress bar */}
          <div className="flex rounded-full overflow-hidden h-2 gap-0.5 mb-2">
            {statusConfig.map((s) => {
              const count = jobs.filter((j) => j.status === s.key).length;
              const pct = (count / total) * 100;
              return (
                <div
                  key={s.key}
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: s.color, minWidth: pct > 0 ? "4px" : "0" }}
                  title={`${s.key}: ${count}`}
                />
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            {statusConfig.map((s) => (
              <div key={s.key} className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="text-xs text-gray-500">{s.key}</span>
              </div>
            ))}
          </div>

          {/* Recent jobs */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Recent Jobs</p>
            {jobs.slice(0, 4).map((j) => {
              const cfg = statusConfig.find((s) => s.key === j.status) || statusConfig[0];
              return (
                <div
                  key={j.id}
                  onClick={() => navigate(`/job-cards/${j.id}`)}
                  className="flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all hover:shadow-sm"
                  style={{ background: "#fffbeb" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#fef3c7"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "#fffbeb"; }}
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{j.title}</p>
                    <p className="text-xs text-gray-400">{j.jobNo}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="w-16 rounded-full h-1.5" style={{ background: "#fef3c7" }}>
                      <div className="h-1.5 rounded-full" style={{ width: `${j.progress}%`, background: cfg.color }} />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{j.progress}%</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}